/*
 * Copyright (c) Nexign, JSC, 2019.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Maxim Fedorov
 * @version 1.0.5
 * @since 1.0.1
 */
import { Component, ReactElement } from 'react';

import { Moment } from 'moment';

import { Th, THead, Tr } from './CalendarTable';
import CalendarTitle from './CalendarTitle';
import { HorizontalSeparateLineStyled } from './styled/CalendarHeadStyled';


export interface IDatePickerLocale {
    monthNames: string[];
    weekLabel: string;
    weekNames: string[];
    yearNames: number[];

}

/**
 * @description Props for calendar head.
 * @interface IRangeDatePickerCalendarHeader
 *
 * @property{calendar} Left of Right Date calendar.
 * @property{handleSelected} Function - change calendar on selector change.
 * @property{locale} Localized strings, customize the date format, and change the first day of week for the calendars.
 * @property{selectComponentProps} Props for Select component
 * @property{showDropdowns} Show picker with two dropdown or one selector.
 * @property{showISOWeekNumbers} Show or not ISO week numbers.
 * @property{showWeekNumbers} Show or not week number on the left side of calendar.
 */
export interface IRangeDatePickerCalendarHeader {
    calendar: Moment;
    handleSelected: (value: Moment) => void;
    leftDate: Moment;
    locale: IDatePickerLocale;
    selectComponentProps: { kind: string };
    showDropdowns: boolean;
    showISOWeekNumbers: boolean;
    showWeekNumbers: boolean;
}

/**
 * CalendarHeader component.
 *
 * Props: IRangeDatePickerCalendarHeader.
 * Render Thead of the DatePicker.
 * Thead contains dropdown (s) for selecting month / year and weeks header.
 */
export default class CalendarHeader extends Component<IRangeDatePickerCalendarHeader> {

    /* render CalendarTitle with Calendar selectors */
    public renderTitle = (): ReactElement => {
        const { calendar, leftDate, locale, showDropdowns, handleSelected, selectComponentProps } = this.props;
        const colSpan = 7;
        const props = {
            calendar,
            colSpan,
            handleSelected,
            leftDate,
            locale,
            selectComponentProps,
            showDropdowns,
        };
        return <CalendarTitle {...props} />;
    };

    /* Render weeks header */
    public renderWeeks = (): ReactElement => {
        const { showWeekNumbers, showISOWeekNumbers, locale } = this.props;
        const { weekNames, weekLabel } = locale;
        const showWeeks = showISOWeekNumbers || showWeekNumbers;
        const weeks = showWeeks ? [weekLabel].concat(weekNames) : weekNames;
        const WeekData = weeks.map((children: any, key: any) => {
            const className = `${key === 0 && showWeeks ? 'week' : ''}`;
            const thProps = {
                children,
                className,
            };
            return <Th key={key} {...thProps} />;
        });
        return <Tr className='day-of-week'>{WeekData}</Tr>;
    };

    public render() {
        return (
            <THead className="range-date-picker__calendar-head-thead">
                <Tr>
                    {this.renderTitle()}
                </Tr>
                {this.renderWeeks()}
                <HorizontalSeparateLineStyled/>
            </THead>
        );
    }
}
